/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=2226-105961&m=dev
 *
 * Full orders tab view composing all 6 sections:
 * 1. OrderState (active_orders cards)
 * 2. ManageOrder (return, cancel order)
 * 3. OrderItems (product list)
 * 4. OrderSummary (price breakdown)
 * 5. PurchaseDetails (addresses, dates, order number)
 * 6. FaqSupport (FAQ accordions)
 */

import FaqSupport from '../sections/FaqSupport'
import ManageOrder from '../sections/ManageOrder'
import OrderItems from '../sections/OrderItems'
import OrderState from '../sections/OrderState'
import OrderSummary from '../sections/OrderSummary'
import PurchaseDetails from '../sections/PurchaseDetails'
import Navigation from '../shared/Navigation'
import OrderHeader from '../shared/OrderHeader'

import type { Environment } from '../../../App'
import { useLabel } from '../../../i18n'
import { applyReturnEligibility } from '../../../utils/orderStateCards'
import { resolveOrderSubtitle } from '../../../utils/orderSubtitle'
import type {
  Order,
  OrderStateCard,
  OrderStateCta,
  TimelineLabels,
} from '../types'

interface OrdersTabViewProps {
  order: Order
  env: Environment
  /** `active_orders` cards for this order, already resolved by the host. */
  orderStateCards?: OrderStateCard[]
  onOrderStateClick?: (card: OrderStateCard, cta: OrderStateCta) => void
  /** Translated timeline captions, supplied by the host. */
  timelineLabels?: TimelineLabels
  isReturnDetails?: boolean
  onBack?: () => void
  role?: 'customer' | 'style-advisor'
  isCancellationFlowEnabled?: boolean
  onFaqOpen?: (title: string) => void
  onReturnClicked?: (orderId: string) => void
  onItemDetailsClick?: (item: Order['items'][number]) => void
  onReturnDetailsClick?: (item: Order['items'][number]) => void
  onCancelContinue?: (items: Order['items'][number][]) => Promise<void> | void
  onCancellationClicked?: () => void
  onCopyProductCode?: (code: string) => void
}

export default function OrdersTabView({
  order,
  env,
  orderStateCards = [],
  onOrderStateClick,
  timelineLabels,
  isReturnDetails = false,
  onBack,
  role = 'customer',
  isCancellationFlowEnabled = true,
  onFaqOpen,
  onReturnClicked,
  onItemDetailsClick,
  onReturnDetailsClick,
  onCancelContinue,
  onCancellationClicked,
  onCopyProductCode,
}: OrdersTabViewProps) {
  const t = useLabel()
  const subtitle = resolveOrderSubtitle(order.items, order.orderDate, t)
  const hasCancellableItems = order.items.some((item) => item.isCancellable)
  const activeCards = applyReturnEligibility(orderStateCards, order.canReturn)
  const hasManageOrderActions =
    role === 'customer' || (isCancellationFlowEnabled && hasCancellableItems)
  // Navigation is the host's, not ours. Building the URL here meant a
  // root-relative `/returns?orderId=…`, which carries no locale: the
  // storefront's `($locale)` route then redirects to `en-us` and the returns
  // page reads its country off that locale, so a GB customer was offered the US
  // returns provider. The host knows the locale and the storefront origin.
  const handleReturnClick = () => {
    onReturnClicked?.(order.orderNumber)
  }

  return (
    <div className="flex flex-col px-16 pb-80 md:pb-120 md:max-lg:mx-auto md:max-w-[522px] lg:px-0 3xl:max-w-[574px]">
      {/* Navigation back button */}
      <Navigation onBack={onBack} />

      <div className="flex flex-col gap-48">
        {/* 0. Order header */}
        <OrderHeader orderNumber={order.orderNumber} subtitle={subtitle} />

        {/* 1. Order state cards from the `active_orders` trait */}
        <OrderState
          cards={activeCards}
          timelineLabels={timelineLabels}
          onCtaClick={onOrderStateClick}
        />

        {/* 2. Manage order actions, above the items so no scrolling is needed */}
        {hasManageOrderActions && (
          <ManageOrder
            isReturnEligible={role === 'customer' ? order.canReturn : false}
            items={order.items}
            env={env}
            role={role}
            isCancellationFlowEnabled={isCancellationFlowEnabled}
            onReturn={handleReturnClick}
            onItemDetailsClick={onItemDetailsClick}
            onReturnDetailsClick={onReturnDetailsClick}
            onCancelContinue={onCancelContinue}
            onCancellationClicked={onCancellationClicked}
          />
        )}

        <div className="flex flex-col gap-16">
          {/* 3. Order items with product cards */}
          <OrderItems
            items={order.items}
            env={env}
            role={role}
            onItemDetailsClick={onItemDetailsClick}
            onReturnDetailsClick={onReturnDetailsClick}
            onCopyProductCode={onCopyProductCode}
          />

          {/* 4. Price breakdown */}
          <OrderSummary
            subtotal={order.subtotal}
            salesTax={order.salesTax}
            shippingChargeAmount={order.shippingChargeAmount}
            total={order.total}
            currency={order.currency}
            shippingAddress={order.shippingAddress}
          />
        </div>

        {/* 5. Purchase details card */}
        <PurchaseDetails order={order} isReturnDetails={isReturnDetails} />

        {/* 6. FAQ & Support accordions */}
        <FaqSupport env={env} onFaqOpen={onFaqOpen} />
      </div>
    </div>
  )
}
